<script lang="ts">
  import { activeAmenities, amenityBlendedScore, scoreClass } from '../lib/scoring';
  import { enabledAmenityKeys } from '../lib/state';
  import AmenityPicker from './AmenityPicker.svelte';
  import type { Candidate } from '../lib/types';

  export let candidate: Candidate;

  let customizing = false;

  $: shown = activeAmenities($enabledAmenityKeys);
  $: score = amenityBlendedScore(candidate, $enabledAmenityKeys);
  $: pending = candidate.amenities && shown.some((a) => !(a.key in candidate.amenities!));

  function statusClass(s: number): string {
    return scoreClass(s) === 'good' ? 'ok' : 'missing';
  }
  function colors(s: number) {
    const cls = scoreClass(s);
    return {
      bg: cls === 'good' ? '#d4edda' : cls === 'mid' ? '#fff3cd' : '#f8d7da',
      fg: cls === 'good' ? 'var(--good)' : cls === 'mid' ? 'var(--mid)' : 'var(--warn)',
    };
  }
  const cls = (min: number, good: number, mid: number) => (min <= good ? 'good' : min <= mid ? 'mid' : 'bad');

  function acres(sqm: number): string {
    const ac = sqm / 4046.86;
    return ac >= 10 ? `${Math.round(ac)} ac` : `${ac.toFixed(1)} ac`;
  }
</script>

<div class="section-title" style="margin-top: 20px;">
  Daily places
  {#if score != null}
    {@const col = colors(score)}
    <span class="key-status {statusClass(score)}" style={`background:${col.bg}; color:${col.fg};`}>{score} / 100</span>
  {/if}
  <button type="button" class="customize-btn" aria-expanded={customizing} on:click={() => (customizing = !customizing)}>
    {customizing ? 'Done' : 'Customize'}
  </button>
</div>

{#if customizing}
  <AmenityPicker />
{/if}

{#if !candidate.amenities}
  <div class="loading">Looking up nearby places...</div>
{:else if shown.length === 0}
  <div class="empty-state">No places selected. Tap Customize to choose what matters to you.</div>
{:else}
  <div class="amenities-grid">
    {#each shown as a (a.key)}
      {@const am = candidate.amenities[a.key]}
      <div class="amenity-row">
        <div class="amenity-label">{a.label}</div>
        {#if !am}
          <div class="amenity-place"><span class="muted">looking up…</span></div>
          <div class="amenity-time muted">…</div>
        {:else if am.error || am.minutes == null}
          <div class="amenity-place"><span class="muted">{(typeof am.error === 'string' && am.error) || 'none found nearby'}</span></div>
          <div class="amenity-time bad">—</div>
        {:else}
          <div class="amenity-place" title={am.name}>
            {am.name}{#if am.areaSqm}<span class="muted area"> · {acres(am.areaSqm)}</span>{/if}
          </div>
          <div class="amenity-time {cls(am.minutes, a.good, a.mid)}">{am.minutes}m walk</div>
        {/if}
      </div>
    {/each}
  </div>
  {#if pending}<div class="loading">Fetching newly selected places…</div>{/if}
{/if}

<style>
  .customize-btn {
    margin-left: auto;
    appearance: none; background: none; border: none; padding: 0; cursor: pointer;
    font: inherit; font-size: 11px; font-weight: 600; letter-spacing: 0; text-transform: none;
    color: var(--accent); text-decoration: underline;
  }
</style>
